import { useEffect, useRef, useState, type ReactNode } from 'react'
import { Loader2, Mail, MapPin, Pencil, Save, UserRound } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Textarea } from '@/components/ui/textarea'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { SignatureDialog } from '@/components/signature/SignatureDialog'
import { useUpdatePatient } from '@/hooks/usePatients'
import { apiErrorMessage } from '@/lib/apiErrorMessage'
import { cn } from '@/lib/utils'
import {
  formatAge,
  formatDate,
  formatSex,
  patientNameParts,
  todayCalendarDate,
} from '@/lib/patient'
import {
  EMPTY_DEMOGRAPHICS,
  LANGUAGES,
  SEX_OPTIONS,
  birthDateError,
  demographicsChanges,
  demographicsFormFrom,
  emailError,
  optionsWith,
  type DemographicFormField,
  type DemographicsChanges,
  type DemographicsForm,
  type FormOption,
} from '@/lib/patientForm'
import type { PatientDetail } from '@/api/patientsApi'

/** Mirrors `@MaxLength` on the server's UpdatePatientDto. */
const REASON_MAX_LENGTH = 2000

const REASON_REQUIRED_MESSAGE = 'Reason for change is required.'

/**
 * A Radix Select cannot hold an empty value, so "Not specified" is this sentinel
 * in the control and blank in the form. It is what lets a correction clear sex or
 * language (z8nrz7dp78 AC2.4).
 */
const NOT_SPECIFIED = '__not_specified__'

/** Compact controls, sized to sit in a row where the value was. */
const CONTROL = 'h-8 text-[13px]'

/**
 * One label-to-value row inside a panel. Rendered as `dt`/`dd` pairs into the
 * parent's two-column grid, so labels and values align across the whole panel
 * rather than each row setting its own widths. While editing, `children` is the
 * input that replaces the value, and the label names it.
 */
function Row({
  label,
  value,
  htmlFor,
  children,
}: {
  label: string
  value?: string | null
  htmlFor?: string
  children?: ReactNode
}) {
  return (
    <>
      <dt className='whitespace-nowrap text-[11px] text-muted-foreground'>
        {htmlFor ? <label htmlFor={htmlFor}>{label}</label> : label}
      </dt>
      {/* Left-aligned against a fixed label column. Right-aligning strands the
          value at the far edge of a wide panel, away from the label it belongs to. */}
      {children ? (
        <dd className='min-w-0 space-y-1'>{children}</dd>
      ) : (
        <dd className={cn('text-[13px]', value ? 'text-foreground' : 'text-muted-foreground/40')}>
          {value ?? 'Not recorded'}
        </dd>
      )}
    </>
  )
}

/** A tinted, icon-headed cluster of related fields. */
function Panel({
  icon: Icon,
  title,
  editing,
  children,
}: {
  icon: typeof UserRound
  title: string
  editing: boolean
  children: ReactNode
}) {
  return (
    <div className='rounded-lg border bg-muted/30 p-3'>
      <div className='mb-2 flex items-center gap-1.5 text-[10px] font-semibold uppercase tracking-wider text-muted-foreground/70'>
        <Icon className='h-3 w-3' />
        {title}
      </div>
      <dl
        className={cn(
          'grid grid-cols-[5.5rem_1fr] gap-x-3',
          editing ? 'items-center gap-y-2' : 'items-baseline gap-y-1.5',
        )}
      >
        {children}
      </dl>
    </div>
  )
}

function FieldError({ id, message }: { id: string; message: string | null }) {
  if (!message) return null
  return (
    <p id={id} role='alert' className='text-xs text-destructive'>
      {message}
    </p>
  )
}

/**
 * The Patient information card on the patient page, read and corrected in place
 * (z8nrz7dp78).
 *
 * Edit turns the values a user may correct into inputs where they stand: the
 * create page's fields and rules. The fields that only ever arrive from the EHR
 * (middle name, race, account number, …) stay as they are. Saving needs a reason
 * and the shared Part 11 signature, and each changed field is recorded in the
 * patient's audit trail with that reason. A save that changes nothing leaves edit
 * mode without asking for a signature; Cancel sends nothing. MRN is not editable.
 */
export function PatientInformationCard({
  patient,
  canEdit,
  signerName,
  autoEdit = false,
  onDone,
}: {
  patient: PatientDetail
  /** Holds patients.edit. The server 403s anyone else. */
  canEdit: boolean
  signerName?: string
  /**
   * Open straight into edit: the patient page shows this card only while a
   * correction is being made, from its header's Edit (z8nrz7g57g).
   */
  autoEdit?: boolean
  /** Called when an edit ends — saved, cancelled, or nothing changed. */
  onDone?: () => void
}) {
  const update = useUpdatePatient(patient.mrn)
  const [editing, setEditing] = useState(false)
  const [original, setOriginal] = useState<DemographicsForm>(EMPTY_DEMOGRAPHICS)
  const [form, setForm] = useState<DemographicsForm>(EMPTY_DEMOGRAPHICS)
  const [reason, setReason] = useState('')
  const [dobError, setDobError] = useState<string | null>(null)
  const [mailError, setMailError] = useState<string | null>(null)
  const [reasonError, setReasonError] = useState<string | null>(null)
  const [signOpen, setSignOpen] = useState(false)
  const [signError, setSignError] = useState<string | null>(null)
  // What the signature is for, fixed when Save asked for it: the fields behind the
  // signature prompt cannot change what gets signed.
  const pending = useRef<{ changes: DemographicsChanges; reason: string } | null>(null)

  // Every edit starts from the record as it is now, with no reason carried over:
  // each correction is its own deliberate act.
  const startEditing = () => {
    const current = demographicsFormFrom(patient)
    setOriginal(current)
    setForm(current)
    setReason('')
    setDobError(null)
    setMailError(null)
    setReasonError(null)
    setSignError(null)
    pending.current = null
    setEditing(true)
  }

  // Opened for a correction: start editing at once.
  useEffect(() => {
    if (autoEdit) startEditing()
    // Once, on mount: a re-render must not restart an edit in progress.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const wasEditing = useRef(false)
  useEffect(() => {
    if (wasEditing.current && !editing) onDone?.()
    wasEditing.current = editing
  }, [editing, onDone])

  const set = (field: DemographicFormField, value: string) => {
    if (field === 'birthDate') setDobError(null)
    if (field === 'email') setMailError(null)
    setForm(f => ({ ...f, [field]: value }))
  }

  const save = () => {
    const changes = demographicsChanges(original, form)
    if (Object.keys(changes).length === 0) {
      setEditing(false)
      return
    }
    const dob = birthDateError(form.birthDate)
    setDobError(dob)
    // Only an email the user changed: a stored one the EHR sent is not theirs to fix
    // before correcting something else.
    const mail = 'email' in changes ? emailError(form.email) : null
    setMailError(mail)
    const trimmed = reason.trim()
    setReasonError(trimmed ? null : REASON_REQUIRED_MESSAGE)
    if (dob || mail || !trimmed) return
    pending.current = { changes, reason: trimmed }
    setSignError(null)
    setSignOpen(true)
  }

  const sign = async (signingToken: string) => {
    const what = pending.current
    if (!what) return
    setSignError(null)
    try {
      await update.mutateAsync({ ...what.changes, reason: what.reason, signingToken })
      setSignOpen(false)
      setEditing(false)
    } catch (err) {
      // Nothing was saved: the record and its audit commit together or not at all.
      setSignError(apiErrorMessage(err, 'The patient could not be updated.'))
    }
  }

  const id = (field: string) => `patient-edit-${field}`
  const text = (field: DemographicFormField, type?: string, error?: string | null) => (
    <>
      <Input
        id={id(field)}
        type={type}
        className={CONTROL}
        value={form[field]}
        aria-invalid={error ? true : undefined}
        aria-describedby={error ? id(`${field}-error`) : undefined}
        onChange={e => set(field, e.target.value)}
      />
      <FieldError id={id(`${field}-error`)} message={error ?? null} />
    </>
  )
  const select = (field: 'sex' | 'preferredLanguage', options: FormOption[]) => (
    <Select
      value={form[field]}
      onValueChange={value => set(field, value === NOT_SPECIFIED ? '' : value)}
    >
      <SelectTrigger id={id(field)} className={CONTROL}>
        <SelectValue placeholder='Not specified' />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value={NOT_SPECIFIED}>Not specified</SelectItem>
        {optionsWith(options, form[field]).map(o => (
          <SelectItem key={o.value} value={o.value}>
            {o.label}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  )
  /** The editable row: an input while editing, the stored value otherwise. */
  const editable = (label: string, field: DemographicFormField, value: string | null, input: ReactNode) =>
    editing ? (
      <Row label={label} htmlFor={id(field)}>
        {input}
      </Row>
    ) : (
      <Row label={label} value={value} />
    )

  const { first, last } = patientNameParts(patient)
  const anyKnown = Boolean(
    first || last || patient.birthDate || patient.sex || patient.email || patient.phone ||
    patient.preferredLanguage || patient.addressLine || patient.city || patient.state ||
    patient.postalCode || patient.country || patient.middleName || patient.race ||
    patient.ethnicity || patient.maritalStatus || patient.businessPhone || patient.accountNumber,
  )

  return (
    <div className='rounded-xl border bg-card p-4'>
      <div className='mb-3 flex items-center gap-2'>
        <UserRound className='h-4 w-4 text-muted-foreground' />
        <h2 className='text-sm font-semibold'>Patient information</h2>
        {canEdit && !editing && (
          <Button variant='outline' size='sm' className='ml-auto h-7' onClick={startEditing}>
            <Pencil className='mr-1.5 h-3.5 w-3.5' />
            Edit
          </Button>
        )}
      </div>

      {/* With nothing on file the panels would be thirteen rows of "Not recorded" —
          taller than a fully populated card and carrying less. The note says it once
          instead. Partially filled records still get the full panels, so a missing
          field is visible against the ones beside it. */}
      {!anyKnown && !editing ? (
        <p className='rounded-lg border border-dashed px-3 py-2 text-xs text-muted-foreground'>
          No demographics on file yet. They arrive automatically with the EHR integration,
          {canEdit ? ' or can be added with Edit.' : ' or can be entered when registering the patient.'}
        </p>
      ) : (
        // noValidate: the rules are checked here and on the server. A stored value
        // the browser's own email check dislikes (from the EHR) must not block
        // correcting a different field.
        <form
          noValidate
          onSubmit={e => {
            e.preventDefault()
            save()
          }}
        >
          {/* Grouped by what the reader came looking for: who the patient is, how to
              reach them, where they are. Address components stay discrete rather than
              flattened into one line, so each is legible and individually absent. */}
          <div className='grid gap-3 lg:grid-cols-3'>
            <Panel icon={UserRound} title='Identity' editing={editing}>
              {editable('First name', 'firstName', first, text('firstName'))}
              <Row label='Middle name' value={patient.middleName} />
              {editable('Last name', 'lastName', last, text('lastName'))}
              {editable(
                'Born',
                'birthDate',
                formatDate(patient.birthDate),
                <>
                  {/* A calendar date: the input's own YYYY-MM-DD is sent verbatim and
                      stored as that day (z8nrz7dp77). */}
                  <Input
                    id={id('birthDate')}
                    type='date'
                    className={CONTROL}
                    value={form.birthDate}
                    max={todayCalendarDate()}
                    aria-invalid={dobError ? true : undefined}
                    aria-describedby={dobError ? id('birthDate-error') : undefined}
                    onChange={e => set('birthDate', e.target.value)}
                  />
                  <FieldError id={id('birthDate-error')} message={dobError} />
                </>,
              )}
              {/* Derived from the date of birth, so while editing it follows the input. */}
              <Row
                label='Age'
                value={formatAge(editing ? form.birthDate || null : patient.birthDate)}
              />
              {editable('Sex', 'sex', formatSex(patient.sex), select('sex', SEX_OPTIONS))}
              {editable(
                'Language',
                'preferredLanguage',
                patient.preferredLanguage,
                select('preferredLanguage', LANGUAGES),
              )}
              <Row label='Race' value={patient.race} />
              <Row label='Ethnicity' value={patient.ethnicity} />
              <Row label='Marital status' value={patient.maritalStatus} />
              <Row label='Account no.' value={patient.accountNumber} />
            </Panel>

            <Panel icon={Mail} title='Contact' editing={editing}>
              {editable('Phone', 'phone', patient.phone, text('phone'))}
              <Row label='Work phone' value={patient.businessPhone} />
              {editable('Email', 'email', patient.email, text('email', 'email', mailError))}
            </Panel>

            <Panel icon={MapPin} title='Address' editing={editing}>
              {editable('Street', 'addressLine', patient.addressLine, text('addressLine'))}
              {editable('City', 'city', patient.city, text('city'))}
              {editable('State', 'state', patient.state, text('state'))}
              {editable('Postal', 'postalCode', patient.postalCode, text('postalCode'))}
              {editable('Country', 'country', patient.country, text('country'))}
            </Panel>
          </div>

          {editing && (
            <div className='mt-3 flex flex-col gap-3 rounded-lg border border-dashed p-3 sm:flex-row sm:items-end'>
              <div className='flex-1 space-y-1.5'>
                <label htmlFor={id('reason')} className='block text-xs font-medium text-muted-foreground'>
                  Reason for change <span className='text-destructive'>*</span>
                </label>
                <Textarea
                  id={id('reason')}
                  rows={2}
                  maxLength={REASON_MAX_LENGTH}
                  value={reason}
                  aria-invalid={reasonError ? true : undefined}
                  aria-describedby={reasonError ? id('reason-error') : undefined}
                  onChange={e => {
                    setReason(e.target.value)
                    setReasonError(null)
                  }}
                  placeholder='Why do these details need correcting?'
                />
                <FieldError id={id('reason-error')} message={reasonError} />
                <p className='text-[11px] text-muted-foreground'>
                  Every change is recorded in the audit trail with your reason and signature.
                </p>
              </div>
              <div className='flex shrink-0 items-center gap-2'>
                <Button
                  type='button'
                  variant='ghost'
                  disabled={update.isPending}
                  onClick={() => setEditing(false)}
                >
                  Cancel
                </Button>
                <Button type='submit' disabled={update.isPending}>
                  {update.isPending ? (
                    <Loader2 className='mr-2 h-4 w-4 animate-spin' />
                  ) : (
                    <Save className='mr-2 h-4 w-4' />
                  )}
                  Save
                </Button>
              </div>
            </div>
          )}
        </form>
      )}

      <SignatureDialog
        open={signOpen}
        onOpenChange={o => {
          if (!o) setSignError(null)
          setSignOpen(o)
        }}
        title='Sign this correction'
        description='Your signature confirms the changes and the reason you gave.'
        confirmLabel='Sign & Save'
        signerName={signerName}
        busy={update.isPending}
        error={signError}
        onConfirm={token => void sign(token)}
      />
    </div>
  )
}
